import React from 'react'
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Flex,
    Progress,
    Row,
    Segmented,
    Skeleton,
    Tag,
    theme,
    Typography,
} from 'antd'
import { ArrowRightOutlined, ExportOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { TimeAgo } from '../../components/time-ago'
import { isReading, useSiteOverview, useStartsReading } from '../hooks'
import { SiteOverview } from '../site'

type SiteProps = {
    siteId: string
}

type TodoMessage = {
    text: string
    link?: React.ReactNode
}

/** Todos about the connection itself, which show as errors instead of warnings. */
const CONNECTION_TODOS = ['disconnected', 'not_seen']

/** What needs attention, shown above the chats so it is seen. */
export function SiteTodos({ siteId }: SiteProps) {
    const { t } = useTranslation()
    const { data } = useSiteOverview(siteId)

    if (!data || data.todos.length === 0) {
        return null
    }

    const snippet = data.connection.type === 'snippet'
    const todoOf = (key: string) => data.todos.find((todo) => todo.key === key)

    const settingsLink = (to: string, label: string) => (
        <Link
            to={to}
            params={{ siteId }}
        >
            {label}
        </Link>
    )

    const knowledgeLink = (search: { status?: string }) => (
        <Link
            to="/websites/$siteId/settings/knowledge"
            params={{ siteId }}
            search={search}
        >
            {t('View pages')}
        </Link>
    )

    const notSeenText = snippet
        ? t('The snippet has not loaded on this website for more than 2 days. Check that it is still installed.')
        : t('Evaa has not heard from this website for more than 2 days. Check that the plugin is active.')

    const messages: Record<string, TodoMessage> = {
        disconnected: {
            text: snippet
                ? t('This website is disconnected. Add it again under Websites to show the chat.')
                : t('This website is disconnected. Connect it again from the Evaa plugin in WordPress.'),
        },
        no_plan: {
            text: t('Choose a plan: the chat is off for visitors.'),
            link: <Link to="/settings">{t('Choose a plan')}</Link>,
        },
        not_installed: {
            text: t('Add the code snippet to your website to show the chat.'),
            link: settingsLink('/websites/$siteId/settings/connection', t('Install')),
        },
        not_seen: {
            text: notSeenText,
            link: snippet ? settingsLink('/websites/$siteId/settings/connection', t('Check')) : undefined,
        },
        chat_disabled: {
            text: t('The chat is switched off.'),
            link: settingsLink('/websites/$siteId/settings', t('Open settings')),
        },
        documents_need_javascript: {
            text: t('Evaa found no text on {{count}} pages.', {
                count: todoOf('documents_need_javascript')?.count ?? 0,
            }),
            link: knowledgeLink({ status: 'needs_javascript' }),
        },
        documents_failed: {
            text: t('{{count}} pages could not be read.', {
                count: todoOf('documents_failed')?.count ?? 0,
            }),
            link: knowledgeLink({ status: 'failed' }),
        },
        page_limit_reached: {
            text: pageLimitText(t, todoOf('page_limit_reached')),
            link: knowledgeLink(data.content.limitReached > 0 ? { status: 'limit_reached' } : {}),
        },
        whatsapp_not_configured: {
            text: t('Choose the WhatsApp number and start message for applying via WhatsApp.'),
            link: settingsLink('/websites/$siteId/settings/whatsapp', t('Open settings')),
        },
    }

    return (
        <Flex
            vertical
            gap="small"
        >
            {data.todos.map((todo) => (
                <Alert
                    key={todo.key}
                    type={CONNECTION_TODOS.includes(todo.key) ? 'error' : 'warning'}
                    showIcon
                    message={messages[todo.key]?.text ?? todo.key}
                    action={messages[todo.key]?.link}
                />
            ))}
        </Flex>
    )
}

type SiteStatsProps = SiteProps & {
    days: 7 | 30
    onDaysChange: (days: 7 | 30) => void
}

type FunnelStep = {
    label: string
    value: number
}

/** The chat figures above the chats. */
export function SiteStats({ siteId, days, onDaysChange }: SiteStatsProps) {
    const { t } = useTranslation()
    const { data, isLoading } = useSiteOverview(siteId)
    const period = days === 30 ? 'last30Days' : 'last7Days'

    if (isLoading || !data) {
        return <Skeleton.Input active />
    }

    const numbers = data.numbers[period]
    const hasVacancies = data.content.openVacancies > 0 || data.content.interviewAgents > 0
    const unansweredColour = numbers.unanswered > 0 ? 'text-orange-600' : ''

    // With vacancies a funnel from chat to apply click; without, what visitors asked.
    const steps: FunnelStep[] = hasVacancies
        ? [
              {
                  label: t('Chats'),
                  value: numbers.chats,
              },
              {
                  label: t('Vacancy recommended'),
                  value: numbers.recommendations,
              },
              {
                  label: t('Clicked apply'),
                  value: numbers.applyClicks,
              },
          ]
        : [
              {
                  label: t('Chats'),
                  value: numbers.chats,
              },
              {
                  label: t('Questions'),
                  value: numbers.visitorMessages,
              },
          ]

    // A funnel from chat to apply click, so the numbers say something about each other.
    return (
        <Flex
            align="center"
            justify="space-between"
            gap="middle"
            wrap
            className="rounded-xl border border-neutral-200 bg-white px-4 py-3"
        >
            <Flex
                align="center"
                gap="large"
                wrap
            >
                {steps.map((step, index) => (
                    <FunnelStepFigure
                        key={step.label}
                        step={step}
                        // Only a funnel shows how a step compares to the chats.
                        chats={index > 0 && hasVacancies ? steps[0].value : undefined}
                    />
                ))}
                <Link
                    to="/websites/$siteId"
                    params={{ siteId }}
                    search={(current: { days?: 7 | 30 }) => ({
                        days: current.days,
                        view: 'questions' as const,
                    })}
                    className="border-l border-neutral-200 pl-6"
                >
                    <Flex vertical>
                        <Typography.Text className={`text-xl font-medium leading-tight ${unansweredColour}`}>
                            {numbers.unanswered}
                        </Typography.Text>
                        <Typography.Text
                            type="secondary"
                            className="text-xs"
                        >
                            {t('Unanswered questions')}
                        </Typography.Text>
                    </Flex>
                </Link>
            </Flex>
            <Segmented
                size="small"
                value={period}
                onChange={(value) => onDaysChange(value === 'last30Days' ? 30 : 7)}
                options={[
                    {
                        label: t('7 days'),
                        value: 'last7Days',
                    },
                    {
                        label: t('30 days'),
                        value: 'last30Days',
                    },
                ]}
            />
        </Flex>
    )
}

type FunnelStepFigureProps = {
    step: FunnelStep
    /** The chats of the funnel, after an arrow and with this step as a share of them. */
    chats?: number
}

function FunnelStepFigure({ step, chats }: FunnelStepFigureProps) {
    const inFunnel = chats !== undefined
    const showsShare = inFunnel && chats > 0

    return (
        <Flex
            align="center"
            gap="large"
        >
            {inFunnel && <ArrowRightOutlined className="text-neutral-300" />}
            <Flex vertical>
                <Typography.Text className="text-xl font-medium leading-tight">{step.value}</Typography.Text>
                <Typography.Text
                    type="secondary"
                    className="text-xs"
                >
                    {step.label}
                    {showsShare && ` · ${Math.round((step.value / chats) * 100)}%`}
                </Typography.Text>
            </Flex>
        </Flex>
    )
}

type SiteContentSummaryProps = SiteProps & {
    onShowFailed?: () => void
    onShowNeedsJavascript?: () => void
    onShowSkipped?: () => void
    onShowLimitReached?: () => void
}

/** How far Evaa got reading the website, above the list of pages: one line, and only problems stand out. */
export function SiteContentSummary({
    siteId,
    onShowFailed,
    onShowNeedsJavascript,
    onShowSkipped,
    onShowLimitReached,
}: SiteContentSummaryProps) {
    const { t } = useTranslation()
    const { data } = useSiteOverview(siteId)
    const starting = useStartsReading(siteId)

    if (!data) {
        return null
    }

    const { fetched, pending, failed, indexed, needsJavascript, skipped, pages, maxPages, limitReached } = data.content
    const total = fetched + pending + failed
    const overLimit = limitReached > 0 || pages >= maxPages
    const canShowLimitReached = onShowLimitReached && limitReached > 0

    // Also while looking for pages, before any page waits to be read: right after "Read again", the crawl may still
    // wait in the queue and then read the sitemap first.
    const reading = isReading(data) || starting

    return (
        <Flex
            vertical
            gap="small"
        >
            <Typography.Text>
                {/* While reading, the pages being read again do not count as known, so "0 of 10" would look like a
                    loss. */}
                {reading
                    ? t('Evaa is reading the website.')
                    : t('Evaa knows {{known}} of {{total}} pages', {
                          known: indexed,
                          total,
                      })}
                {skipped > 0 && (
                    <Typography.Text type="secondary">
                        {' · '}
                        {onShowSkipped ? (
                            <Typography.Link onClick={onShowSkipped}>
                                {t('{{count}} skipped', { count: skipped })}
                            </Typography.Link>
                        ) : (
                            t('{{count}} skipped', { count: skipped })
                        )}
                    </Typography.Text>
                )}
                {!reading && data.connection.lastSyncedAt && (
                    <Typography.Text type="secondary">
                        {' · '}
                        {t('updated')} <TimeAgo date={data.connection.lastSyncedAt} />
                    </Typography.Text>
                )}
            </Typography.Text>
            {reading ? (
                <ReadingProgress content={data.content} />
            ) : (
                <PagesUsed
                    pages={pages}
                    maxPages={maxPages}
                />
            )}
            {overLimit && (
                <Alert
                    type="warning"
                    showIcon
                    message={pageLimitText(t, {
                        count: limitReached,
                        max: maxPages,
                    })}
                    description={t(
                        'Skip pages Evaa does not need, such as news, to make room. Evaa reads the other pages the next time it reads the website.'
                    )}
                    action={
                        canShowLimitReached && (
                            <Button
                                size="small"
                                onClick={onShowLimitReached}
                            >
                                {t('View')}
                            </Button>
                        )
                    }
                />
            )}
            {failed > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('{{count}} pages could not be read.', { count: failed })}
                    action={
                        onShowFailed && (
                            <Button
                                size="small"
                                onClick={onShowFailed}
                            >
                                {t('View')}
                            </Button>
                        )
                    }
                />
            )}
            {needsJavascript > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('Evaa found no text on {{count}} pages.', {
                        count: needsJavascript,
                    })}
                    description={t(
                        'The text of these pages is added in the browser by a script, which Evaa cannot read yet. The chat still works there. Ask your website builder to make these pages so the text is in the page itself, or contact us.'
                    )}
                    action={
                        onShowNeedsJavascript && (
                            <Button
                                size="small"
                                onClick={onShowNeedsJavascript}
                            >
                                {t('View')}
                            </Button>
                        )
                    }
                />
            )}
        </Flex>
    )
}

/** Without pages to count yet, a full moving bar: Evaa is busy, but how far is not known. */
function ReadingProgress({ content }: { content: SiteOverview['content'] }) {
    const { t } = useTranslation()
    const { fetched, pending, failed, indexed } = content
    const total = fetched + pending + failed
    const readingPages = pending > 0 || indexed < fetched

    const description = readingPages
        ? t('Evaa is reading {{count}} pages. This takes a few minutes.', { count: pending + (fetched - indexed) })
        : t('Evaa is looking for pages. This takes a few minutes.')

    return (
        <Flex
            vertical
            gap={0}
        >
            <Progress
                percent={readingPages ? Math.round((indexed / Math.max(1, total)) * 100) : 100}
                status="active"
                size="small"
                showInfo={false}
            />
            <Typography.Text
                type="secondary"
                className="text-xs"
            >
                {description}
            </Typography.Text>
        </Flex>
    )
}

type PagesUsedProps = {
    pages: number
    maxPages: number
}

function PagesUsed({ pages, maxPages }: PagesUsedProps) {
    const { t } = useTranslation()
    const { token } = theme.useToken()

    // Orange from 90%, so the limit is seen before pages are left out.
    const nearLimit = pages >= maxPages * 0.9

    return (
        <Flex
            vertical
            gap={0}
        >
            <Progress
                percent={Math.min(100, Math.round((pages / Math.max(1, maxPages)) * 100))}
                size="small"
                showInfo={false}
                strokeColor={nearLimit ? token.colorWarning : undefined}
            />
            <Typography.Text
                type="secondary"
                className="text-xs"
            >
                {t('{{count}} of {{max}} pages used.', {
                    count: pages,
                    max: maxPages,
                })}
            </Typography.Text>
        </Flex>
    )
}

/** The plugin connection and this month's usage. */
export function SiteConnection({ siteId }: SiteProps) {
    const { data, isLoading } = useSiteOverview(siteId)

    if (isLoading || !data) {
        return <Skeleton active />
    }

    return (
        <Row gutter={[16, 16]}>
            <Col
                xs={24}
                lg={12}
            >
                <ConnectionCard connection={data.connection} />
            </Col>
            <Col
                xs={24}
                lg={12}
            >
                <UsageCard usage={data.usage} />
            </Col>
        </Row>
    )
}

type Connection = SiteOverview['connection']

function ConnectionCard({ connection }: { connection: Connection }) {
    const { t } = useTranslation()
    const snippet = connection.type === 'snippet'

    const pluginItem = {
        key: 'plugin',
        label: t('Plugin version'),
        children: <PluginVersion connection={connection} />,
    }

    return (
        <Card
            title={snippet ? t('Code snippet') : t('WordPress plugin')}
            className="h-full"
            extra={
                connection.wordpressAdminUrl && (
                    <Typography.Link
                        href={connection.wordpressAdminUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('Open in WordPress')} <ExportOutlined />
                    </Typography.Link>
                )
            }
        >
            <Descriptions
                column={1}
                size="small"
                items={[
                    {
                        key: 'chat',
                        label: t('Chat on the website'),
                        children: connection.chatEnabled ? t('On') : t('Off'),
                    },
                    {
                        key: 'seen',
                        label: snippet ? t('Snippet last loaded') : t('Last contact'),
                        children: connection.lastSeenAt ? <TimeAgo date={connection.lastSeenAt} /> : t('Never'),
                    },
                    {
                        key: 'synced',
                        label: snippet ? t('Last read') : t('Last sync'),
                        children: <LastSync connection={connection} />,
                    },
                    ...(snippet ? [] : [pluginItem]),
                ]}
            />
        </Card>
    )
}

function LastSync({ connection }: { connection: Connection }) {
    const { t } = useTranslation()

    if (connection.crawling) {
        return t('Reading now')
    }

    if (!connection.lastSyncedAt) {
        return t('Never')
    }

    return <TimeAgo date={connection.lastSyncedAt} />
}

function PluginVersion({ connection }: { connection: Connection }) {
    const { t } = useTranslation()

    return (
        <Flex
            gap="small"
            align="center"
            wrap
        >
            {connection.pluginVersion ?? '–'}
            {connection.pluginOutdated ? (
                <Tag color="orange">
                    {t('Update available: {{version}}', { version: connection.latestPluginVersion })}
                </Tag>
            ) : (
                connection.pluginVersion && <Tag color="green">{t('Up to date')}</Tag>
            )}
        </Flex>
    )
}

type Usage = SiteOverview['usage']

/** The share of the allowance used, at least a sliver so a few messages still show. */
function usagePercent(usage: Usage): number {
    const sliver = usage.companyMessages > 0 ? 2 : 0
    const percent = Math.round((usage.companyMessages / Math.max(1, usage.allowance)) * 100)

    return Math.min(100, Math.max(sliver, percent))
}

function UsageCard({ usage }: { usage: Usage }) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language.replace('_', '-')
    const isUsedUp = usage.companyMessages >= usage.allowance && !usage.extraMessages

    const afterAllowance = usage.extraMessages
        ? t('Once your allowance is used up, you pay per extra chat message, up to your maximum.')
        : t('Once your allowance is used up, the chat is unavailable until it renews.')

    return (
        <Card
            title={t('Usage this billing period')}
            className="h-full"
        >
            <Typography.Text>
                {t('{{used}} of {{allowance}} chat messages used, across all your websites', {
                    used: usage.companyMessages.toLocaleString(locale),
                    allowance: usage.allowance.toLocaleString(locale),
                })}
            </Typography.Text>
            <Progress
                percent={usagePercent(usage)}
                showInfo={false}
                status={isUsedUp ? 'exception' : 'normal'}
            />
            <Typography.Paragraph
                type="secondary"
                className="mb-0 text-sm"
            >
                {t('{{count}} of them on this website.', { count: usage.visitorMessages })}{' '}
                {usage.resetsAt &&
                    t('Your allowance renews on {{date}}.', {
                        date: dayjs(usage.resetsAt).format('D MMMM'),
                    })}{' '}
                {afterAllowance} <Link to="/settings">{t('Change')}</Link>
            </Typography.Paragraph>
        </Card>
    )
}

type PageLimitTodo = {
    count?: number
    max?: number
}

/**
 * What the page limit left out. The crawler does not keep the pages over the limit, so there it can only say it
 * stopped.
 */
function pageLimitText(t: TFunction, todo?: PageLimitTodo) {
    const max = todo?.max ?? 500

    if (todo?.count) {
        return t('Your website has more than {{max}} pages, so Evaa does not read {{count}} of them.', {
            count: todo.count,
            max,
        })
    }

    return t('Evaa reads up to {{max}} pages of a website and has reached that limit.', { max })
}
